import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import TrocarSenhaModal from './TrocarSenhaModal'
import AtualizarFotoPerfilModal from './AtualizarFotoPerfilModal'
import { useSidebar } from '../../contexts/SidebarContext'

interface SidebarProps {
  isOpen: boolean
  onToggle: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const { signOut, user } = useAuth()
  const { hasPermission, loading: permissionsLoading } = usePermissions()
  const { isCollapsed, toggleCollapse } = useSidebar()
  const navigate = useNavigate()
  const location = useLocation()
  const activeRoute = location.pathname
  const [userProfile, setUserProfile] = React.useState<string>('Carregando...')
  const [mostrarAlterarSenha, setMostrarAlterarSenha] = React.useState(false)
  const [mostrarAtualizarFoto, setMostrarAtualizarFoto] = React.useState(false)
  const [usuarioId, setUsuarioId] = React.useState('')
  const [usuarioNome, setUsuarioNome] = React.useState('')
  const [fotoPerfilUrl, setFotoPerfilUrl] = React.useState<string | null>(null)
  const [showTopShadow, setShowTopShadow] = React.useState(false)
  const [showBottomIndicator, setShowBottomIndicator] = React.useState(true)
  const [userMenuOpen, setUserMenuOpen] = React.useState(false)
  const navRef = React.useRef<HTMLDivElement>(null)
  const userMenuRef = React.useRef<HTMLDivElement>(null)

  // Verificar se algum submenu de Configurações está ativo
  const isConfigRouteActive = React.useMemo(() => {
    const configRoutes = ['/vision/contratos', '/vision/servicos', '/vision/usuarios', '/vision/perfis']
    return configRoutes.some(route => activeRoute.startsWith(route))
  }, [activeRoute])

  // Verificar se algum submenu de KPIs está ativo
  const isKpisRouteActive = React.useMemo(() => {
    return activeRoute.startsWith('/vision/kpis')
  }, [activeRoute])

  // Manter menus expandidos se algum submenu estiver ativo
  const [configOpen, setConfigOpen] = React.useState(isConfigRouteActive)
  const [kpisOpen, setKpisOpen] = React.useState(isKpisRouteActive)

  // Buscar perfil do usuário
  React.useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.email) {
        setUserProfile('Usuário')
        return
      }

      try {
        const response = await fetch(`/api/gestao/usuarios?email=${encodeURIComponent(user.email)}`)
        if (response.ok) {
          const userData = await response.json()
          setUserProfile(userData.perfil?.nome || 'Sem perfil')
          setUsuarioId(userData.id)
          setUsuarioNome(userData.nome_completo || userData.nome || 'Usuário')
          setFotoPerfilUrl(userData.foto_perfil_url || null)
        } else {
          setUserProfile('Usuário')
        }
      } catch (error) {
        console.error('Erro ao buscar perfil:', error)
        setUserProfile('Usuário')
      }
    }

    fetchUserProfile()
  }, [user])

  // Atualizar estado quando rota mudar
  React.useEffect(() => {
    if (isConfigRouteActive) {
      setConfigOpen(true)
    }
    if (isKpisRouteActive) {
      setKpisOpen(true)
    }
  }, [isConfigRouteActive, isKpisRouteActive])

  const handleSignOut = async () => {
    console.log('🚪 Iniciando logout...')
    
    try {
      // Executar signOut do Supabase
      await signOut()
      console.log('✅ SignOut do Supabase executado')
    } catch (err) {
      console.error('Erro no signOut:', err)
    }
    
    // Limpar TODOS os dados do localStorage relacionados ao Supabase
    console.log('🧹 Limpando localStorage...')
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.includes('supabase') || key.includes('auth')) {
        localStorage.removeItem(key)
        console.log('Removido:', key)
      }
    })
    
    // Limpar sessionStorage também
    sessionStorage.clear()
    
    console.log('🔄 Redirecionando e recarregando página...')
    // Usar replace para forçar reload completo
    window.location.replace('/vision/login')
  }

  const navigateTo = (path: string) => {
    navigate(path)
  }

  const toggleConfig = () => {
    setConfigOpen(!configOpen)
  }

  const toggleKpis = () => {
    setKpisOpen(!kpisOpen)
  }

  const handleAlterarSenha = () => {
    setMostrarAlterarSenha(true)
  }

  const handleAlterarSenhaSuccess = () => {
    setMostrarAlterarSenha(false)
    alert('Senha alterada com sucesso!')
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget
    const scrollTop = element.scrollTop
    const scrollHeight = element.scrollHeight
    const clientHeight = element.clientHeight
    
    // Mostrar sombra no topo se tiver scroll
    setShowTopShadow(scrollTop > 10)
    
    // Mostrar indicador de mais conteúdo se não estiver no final
    setShowBottomIndicator(scrollTop + clientHeight < scrollHeight - 10)
  }

  // Verificar scroll inicial quando menu expandir/colapsar
  React.useEffect(() => {
    const checkScroll = () => {
      if (navRef.current) {
        const element = navRef.current
        const hasScroll = element.scrollHeight > element.clientHeight
        const scrollTop = element.scrollTop
        const scrollHeight = element.scrollHeight
        const clientHeight = element.clientHeight
        
        setShowBottomIndicator(hasScroll && (scrollTop + clientHeight < scrollHeight - 10))
        setShowTopShadow(scrollTop > 10)
      }
    }
    
    checkScroll()
    const timer = setTimeout(checkScroll, 100)
    
    return () => clearTimeout(timer)
  }, [kpisOpen, configOpen, isCollapsed])

  // Fechar dropdown ao clicar fora
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [userMenuOpen])

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      path: '/vision/dashboard',
      permission: 'dashboard.visualizar'
    },
    {
      id: 'clientes',
      label: 'Clientes',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      path: '/vision/clientes',
      permission: 'clientes.visualizar'
    },
    {
      id: 'inteligencia-mercado',
      label: 'Inteligência de Mercado',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      path: '/vision/inteligencia-mercado',
      permission: 'dashboard.visualizar'
    },
    {
      id: 'planos',
      label: 'Planos',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/vision/planos',
      permission: 'planos.visualizar'
    },
    {
      id: 'propostas',
      label: 'Propostas',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z M7 8h10" />
        </svg>
      ),
      path: '/vision/propostas',
      permission: 'propostas.visualizar'
    },
    {
      id: 'recebiveis',
      label: 'Recebíveis',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      path: '/vision/recebiveis',
      permission: 'recebiveis.visualizar'
    },
    {
      id: 'comparacao',
      label: 'Comparação Anual',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      path: '/vision/recebiveis/comparacao',
      permission: 'comparacao.visualizar'
    },
    {
      id: 'relatorios',
      label: 'Relatórios',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/vision/relatorios',
      permission: 'relatorios.visualizar'
    }
  ]

  const kpisMenuItems = [
    {
      id: 'kpis-dashboard',
      label: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      path: '/vision/kpis/dashboard',
      permission: 'kpis.dashboard.visualizar'
    },
    {
      id: 'kpis-previsibilidade',
      label: 'Previsibilidade',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      path: '/vision/kpis/previsibilidade',
      permission: 'kpis.dashboard.visualizar'
    },
    {
      id: 'kpis-indicadores',
      label: 'Indicadores',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      ),
      path: '/vision/kpis/indicadores',
      permission: 'kpis.indicadores.visualizar'
    },
    {
      id: 'kpis-configuracao',
      label: 'Configuração',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      path: '/vision/kpis/configuracao',
      permission: 'kpis.configuracao.visualizar'
    },
    {
      id: 'kpis-ordenacao',
      label: 'Ordenação',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>
      ),
      path: '/vision/kpis/ordenacao',
      permission: 'kpis.ordenacao.visualizar'
    },
    {
      id: 'kpis-planejamento',
      label: 'Planejamento',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      path: '/vision/kpis/planejamento',
      permission: 'kpis.planejamento.visualizar'
    },
    {
      id: 'kpis-resultados',
      label: 'Resultados',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      path: '/vision/kpis/resultados',
      permission: 'kpis.resultados.visualizar'
    },
    {
      id: 'kpis-indicadores-calculados',
      label: 'Indicadores Calculados',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      path: '/vision/kpis/indicadores-calculados',
      permission: 'kpis.dashboard.visualizar'
    }
  ]

  const configMenuItems = [
    {
      id: 'contratos',
      label: 'Contratos',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/vision/contratos',
      permission: 'contratos.visualizar'
    },
    {
      id: 'servicos',
      label: 'Serviços',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M9 1v6m6-6v6" />
        </svg>
      ),
      path: '/vision/servicos',
      permission: 'servicos.visualizar'
    },
    {
      id: 'usuarios',
      label: 'Usuários',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      path: '/vision/usuarios',
      permission: 'usuarios.visualizar'
    },
    {
      id: 'perfis',
      label: 'Perfis',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      path: '/vision/perfis',
      permission: 'perfis.visualizar'
    },
    {
      id: 'segmentos',
      label: 'Segmentos',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
        </svg>
      ),
      path: '/vision/configuracoes/segmentos',
      permission: 'configuracoes.visualizar'
    }
  ]

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full bg-gradient-to-b from-slate-900 via-orange-900 to-red-900 transform transition-all duration-300 ease-in-out z-50 flex flex-col ${
        isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      } ${isCollapsed ? 'w-20' : 'w-80'}`}>
        
        {/* Header */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-center w-full">
              <img 
                src="https://static.readdy.ai/image/60d079db6e314477a5615b4d3efd8435/1aa70460cb4235142615a402493549d1.png" 
                alt="Vision 360" 
                className={`transition-all duration-300 ${isCollapsed ? 'h-8 w-8' : 'h-10 w-auto'}`}
              />
              {!isCollapsed && (
                <div className="ml-3">
                  <h1 className="text-white font-bold text-lg">Vision 360</h1>
                  <p className="text-orange-200 text-sm">Inteligência de Performance</p>
                </div>
              )}
            </div>
            
            {/* Close button for mobile */}
            <button
              onClick={onToggle}
              className="lg:hidden text-white hover:text-orange-300 transition-colors duration-200 absolute right-4 top-6"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Toggle Collapse Button - Desktop Only */}
          <button
            onClick={toggleCollapse}
            className="hidden lg:flex absolute -right-3 top-6 bg-orange-500 text-white p-1.5 rounded-full shadow-lg hover:bg-orange-600 transition-all duration-200 z-10 items-center justify-center"
            title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isCollapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              )}
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 relative overflow-hidden">
          {/* Gradiente superior indicando scroll */}
          {showTopShadow && (
            <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-slate-900 via-slate-900/80 to-transparent pointer-events-none z-10" />
          )}
          
          <nav 
            ref={navRef}
            onScroll={handleScroll}
            className="h-full px-4 py-6 space-y-2 overflow-y-auto custom-scrollbar"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(249, 115, 22, 0.5) transparent'
            }}
          >
          {menuItems.map((item: any) => {
            const isMaster = user?.email === 'marcio@m4digital.co'
            
            // Enquanto carrega permissões, não mostrar menus (exceto para master)
            if (permissionsLoading && !isMaster) {
              return null
            }
            
            // Após carregar, verificar permissão
            if (item.permission && !hasPermission(item.permission) && !isMaster) {
              return null
            }
            
            return (
            <button
              key={item.id}
              onClick={() => navigateTo(item.path)}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-4'} py-3 text-left rounded-lg transition-all duration-200 group relative ${
                activeRoute === item.path
                  ? 'bg-orange-500/20 text-orange-300 border-r-4 border-orange-400'
                  : 'text-gray-300 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className={`${isCollapsed ? '' : 'mr-3'} transition-colors duration-200 ${
                activeRoute === item.path 
                  ? 'text-orange-400' 
                  : 'text-gray-400 group-hover:text-orange-300'
              }`}>
                {item.icon}
              </span>
              {!isCollapsed && <span className="font-medium">{item.label}</span>}
              
              {/* Tooltip quando colapsado */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                  {item.label}
                </div>
              )}
              
              {/* Active indicator */}
              {!isCollapsed && activeRoute === item.path && (
                <div className="ml-auto">
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                </div>
              )}
            </button>
            )
          })}

          {/* Menu KPIs Colapsável */}
          <div className="pt-2">
            <button
              onClick={toggleKpis}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'justify-between px-4'} py-3 text-left rounded-lg transition-all duration-200 group relative ${
                isKpisRouteActive ? 'bg-orange-500/20 text-orange-300' : 'text-gray-300 hover:bg-white/10 hover:text-white'
              }`}
            >
              <div className="flex items-center">
                <span className={`${isCollapsed ? '' : 'mr-3'} ${isKpisRouteActive ? 'text-orange-400' : 'text-gray-400 group-hover:text-orange-300'}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                  </svg>
                </span>
                {!isCollapsed && <span className="font-medium">KPIs</span>}
              </div>
              {!isCollapsed && (
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${kpisOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
              {/* Tooltip quando colapsado */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                  KPIs
                </div>
              )}
            </button>

            {/* Submenus de KPIs */}
            {!isCollapsed && kpisOpen && (
              <div className="ml-4 mt-1 space-y-1 border-l-2 border-white/10 pl-2">
                {kpisMenuItems.map((item: any) => {
                  const isMaster = user?.email === 'marcio@m4digital.co'
                  
                  if (permissionsLoading && !isMaster) {
                    return null
                  }
                  
                  if (item.permission && !hasPermission(item.permission) && !isMaster) {
                    return null
                  }
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigateTo(item.path)}
                      className={`w-full flex items-center px-4 py-2 text-left rounded-lg transition-all duration-200 group text-sm relative ${
                        activeRoute === item.path
                          ? 'bg-orange-500/20 text-orange-300'
                          : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'
                      }`}
                    >
                      <span className={`mr-2 transition-colors duration-200 ${
                        activeRoute === item.path 
                          ? 'text-orange-400' 
                          : 'text-gray-500 group-hover:text-gray-400'
                      }`}>
                        {item.icon}
                      </span>
                      <span className="font-medium">{item.label}</span>
                      
                      {activeRoute === item.path && (
                        <div className="ml-auto">
                          <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Menu Configurações Colapsável */}
          <div className="pt-2">
            <button
              onClick={toggleConfig}
              className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'justify-between px-4'} py-3 text-left rounded-lg transition-all duration-200 group relative text-gray-300 hover:bg-white/10 hover:text-white`}
            >
              <div className="flex items-center">
                <span className={`${isCollapsed ? '' : 'mr-3'} text-gray-400 group-hover:text-orange-300`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </span>
                {!isCollapsed && <span className="font-medium">Configurações</span>}
              </div>
              {!isCollapsed && (
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${configOpen ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
              {/* Tooltip quando colapsado */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                  Configurações
                </div>
              )}
            </button>

            {/* Submenus de Configurações */}
            {!isCollapsed && configOpen && (
              <div className="ml-4 mt-1 space-y-1 border-l-2 border-white/10 pl-2">
                {configMenuItems.map((item: any) => {
                  const isMaster = user?.email === 'marcio@m4digital.co'
                  
                  // Enquanto carrega permissões, não mostrar menus (exceto para master)
                  if (permissionsLoading && !isMaster) {
                    return null
                  }
                  
                  // Após carregar, verificar permissão
                  if (item.permission && !hasPermission(item.permission) && !isMaster) {
                    return null
                  }
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigateTo(item.path)}
                      className={`w-full flex items-center px-4 py-2 text-left rounded-lg transition-all duration-200 group text-sm ${
                        activeRoute === item.path
                          ? 'bg-blue-500/20 text-blue-300'
                          : 'text-gray-400 hover:bg-white/5 hover:text-gray-300'
                      }`}
                    >
                      <span className={`mr-2 transition-colors duration-200 ${
                        activeRoute === item.path 
                          ? 'text-blue-400' 
                          : 'text-gray-500 group-hover:text-gray-400'
                      }`}>
                        {item.icon}
                      </span>
                      <span className="font-medium">{item.label}</span>
                      
                      {activeRoute === item.path && (
                        <div className="ml-auto">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
          </nav>
          
          {/* Gradiente inferior + Seta indicando mais conteúdo */}
          {showBottomIndicator && (
            <>
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent pointer-events-none z-10" />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 animate-bounce pointer-events-none z-20">
                <svg className="w-6 h-6 text-orange-400 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </>
          )}
        </div>

        {/* User Section - Compact with Dropdown */}
        <div className="p-4 border-t border-white/10" ref={userMenuRef}>
          <div className="relative">
            {/* User Info - Clickable */}
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className={`w-full flex items-center p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all duration-200 ${isCollapsed ? 'justify-center' : ''}`}
            >
              {fotoPerfilUrl ? (
                <img
                  src={fotoPerfilUrl}
                  alt="Foto de perfil"
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-orange-400"
                />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-r from-orange-400 to-red-400 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-semibold text-sm">
                    {usuarioNome?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              {!isCollapsed && (
                <>
                  <div className="ml-3 flex-1 text-left">
                    <p className="text-white text-sm font-medium truncate">
                      {usuarioNome || user?.email || 'Usuário'}
                    </p>
                    <p className="text-gray-400 text-xs">{userProfile}</p>
                  </div>
                  <svg 
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>

            {/* Dropdown Menu */}
            {!isCollapsed && userMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-slate-800 rounded-lg shadow-xl border border-white/10 overflow-hidden z-50">
                {/* Atualizar Foto de Perfil */}
                <button
                  onClick={() => {
                    setMostrarAtualizarFoto(true)
                    setUserMenuOpen(false)
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-300 hover:bg-orange-500/20 hover:text-orange-300 transition-all duration-200 group"
                >
                  <span className="mr-3 text-gray-400 group-hover:text-orange-400 transition-colors duration-200">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </span>
                  <span className="font-medium">Atualizar Foto de Perfil</span>
                </button>
                
                {/* Divider */}
                <div className="border-t border-white/10"></div>
                
                {/* Alterar Senha */}
                <button
                  onClick={() => {
                    handleAlterarSenha()
                    setUserMenuOpen(false)
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-300 hover:bg-orange-500/20 hover:text-orange-300 transition-all duration-200 group"
                >
                  <span className="mr-3 text-gray-400 group-hover:text-orange-400 transition-colors duration-200">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                  </span>
                  <span className="font-medium">Alterar Senha</span>
                </button>
                
                {/* Divider */}
                <div className="border-t border-white/10"></div>
                
                {/* Sair do Sistema */}
                <button
                  onClick={() => {
                    handleSignOut()
                    setUserMenuOpen(false)
                  }}
                  className="w-full flex items-center px-4 py-3 text-gray-300 hover:bg-red-500/20 hover:text-red-300 transition-all duration-200 group"
                >
                  <span className="mr-3 text-gray-400 group-hover:text-red-400 transition-colors duration-200">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </span>
                  <span className="font-medium">Sair do Sistema</span>
                </button>
              </div>
            )}

            {/* Tooltip quando colapsado */}
            {isCollapsed && (
              <div className="absolute left-full ml-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                Menu do Usuário
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <p className="text-center text-xs text-gray-400">
            © 2026 M4 Digital
          </p>
        </div>
      </div>

      {/* Modal de Alterar Senha */}
      {mostrarAlterarSenha && usuarioId && (
        <TrocarSenhaModal
          usuarioId={usuarioId}
          usuarioNome={usuarioNome}
          onSuccess={handleAlterarSenhaSuccess}
          onCancel={() => setMostrarAlterarSenha(false)}
        />
      )}

      {/* Modal de Atualizar Foto de Perfil */}
      {mostrarAtualizarFoto && usuarioId && (
        <AtualizarFotoPerfilModal
          usuarioId={usuarioId}
          fotoAtual={fotoPerfilUrl}
          onClose={() => setMostrarAtualizarFoto(false)}
          onSuccess={(novaFotoUrl) => {
            setFotoPerfilUrl(novaFotoUrl || null)
            setMostrarAtualizarFoto(false)
          }}
        />
      )}
    </>
  )
}

export default Sidebar
